import React, { Component } from "react";
import { Table, Spinner, Input, Button, Badge } from "reactstrap";
import { toast } from "react-toastify";

import {
  getKeyAccountPresettings,
  updateKeyAccountPresetting,
  getKeyAccountManagers,
  searchUsersForKeyAccountToggle,
  grantKeyAccountTogglePermission,
  revokeKeyAccountTogglePermission,
} from "../../services/keyAccountPresettingsService";

const BORDER = "rgba(255, 255, 255, 0.12)";
const tableStyle = { maxWidth: 400, borderColor: BORDER };
const headerCellStyle = { borderColor: BORDER };

const LVM_SUFFIX = "_level_value_multiplier";
const isLvmName = (name) => typeof name === "string" && name.endsWith(LVM_SUFFIX);
const lvmGestureLabel = (name) => name.slice(0, -LVM_SUFFIX.length);

class KeyAccountPresettingsTab extends Component {
  state = {
    lvmGestures: [],
    loading: true,
    saving: false,
    managers: [],
    loadingManagers: true,
    searchQuery: "",
    searchResults: [],
    searching: false,
    grantingId: null,
    revokingId: null,
  };

  async componentDidMount() {
    await Promise.all([this.fetchPresettings(), this.fetchManagers()]);
  }

  handleLvmChange = (id, value) => {
    this.setState((prev) => ({
      lvmGestures: prev.lvmGestures.map((gesture) => {
        if (gesture.id !== id) return gesture;
        return { ...gesture, value: value === "" ? null : value };
      }),
    }));
  };

  onSave = async () => {
    const { lvmGestures } = this.state;
    this.setState({ saving: true });
    try {
      const updates = lvmGestures
        .filter((g) => g.id)
        .map((gesture) => {
          const floatValue = gesture.value === "" || gesture.value == null ? null : parseFloat(gesture.value);
          return updateKeyAccountPresetting(gesture.id, { name: gesture.name, value: floatValue });
        });
      await Promise.all(updates);
      toast.success("Saved key account presettings");
      await this.fetchPresettings();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      this.setState({ saving: false });
    }
  };

  onSearchQueryChange = (e) => {
    this.setState({ searchQuery: e.target.value, searchResults: [] });
  };

  onSearch = async () => {
    const { searchQuery } = this.state;
    if (!searchQuery.trim()) return;
    this.setState({ searching: true, searchResults: [] });
    try {
      const { data } = await searchUsersForKeyAccountToggle(searchQuery.trim());
      this.setState({ searchResults: Array.isArray(data) ? data : [] });
    } catch (e) {
      toast.error("Search failed");
    } finally {
      this.setState({ searching: false });
    }
  };

  onSearchKeyDown = (e) => {
    if (e.key === "Enter") this.onSearch();
  };

  onGrant = async (user) => {
    this.setState({ grantingId: user.id });
    try {
      await grantKeyAccountTogglePermission(user.id);
      toast.success(`Permission granted to ${user.email || user.username}`);
      this.setState((prev) => ({
        managers: [...prev.managers, { id: user.id, username: user.username, email: user.email }],
        searchResults: prev.searchResults.map((u) => (u.id === user.id ? { ...u, has_permission: true } : u)),
      }));
    } catch (e) {
      toast.error("Grant failed");
    } finally {
      this.setState({ grantingId: null });
    }
  };

  onRevoke = async (userId) => {
    this.setState({ revokingId: userId });
    try {
      await revokeKeyAccountTogglePermission(userId);
      toast.success("Permission revoked");
      this.setState((prev) => ({
        managers: prev.managers.filter((u) => u.id !== userId),
        searchResults: prev.searchResults.map((u) => (u.id === userId ? { ...u, has_permission: false } : u)),
      }));
    } catch (e) {
      toast.error("Revoke failed");
    } finally {
      this.setState({ revokingId: null });
    }
  };

  async fetchPresettings() {
    try {
      const { data } = await getKeyAccountPresettings();
      const lvmGestures = [];
      if (Array.isArray(data)) {
        data.forEach((item) => {
          if (!item || !item.id || !item.name) return;
          if (isLvmName(item.name)) {
            lvmGestures.push({ id: item.id, name: item.name, value: item.value });
          }
        });
        lvmGestures.sort((a, b) => (a.id == null || b.id == null ? 0 : a.id - b.id));
      }
      this.setState({ lvmGestures, loading: false });
    } catch (e) {
      toast.error("Failed to load key account presettings");
      this.setState({ loading: false });
    }
  }

  async fetchManagers() {
    try {
      const { data } = await getKeyAccountManagers();
      this.setState({ managers: Array.isArray(data) ? data : [], loadingManagers: false });
    } catch (e) {
      this.setState({ loadingManagers: false });
    }
  }

  renderManagersSection() {
    const { managers, loadingManagers, searchQuery, searchResults, searching, grantingId, revokingId } = this.state;

    return (
      <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${BORDER}` }}>
        <h6 className="text-uppercase mb-1">Toggle permission</h6>
        <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
          Controls who can manually flip the Key Account flag on a store.{" "}
          <em>The admin_editor group already has this permission by default.</em>
        </p>

        <h6 className="mb-2" style={{ fontSize: "0.8rem" }}>
          Individual grants
        </h6>
        {loadingManagers ? (
          <Spinner size="sm" color="primary" />
        ) : managers.length === 0 ? (
          <p className="text-muted" style={{ fontSize: "0.85rem" }}>
            No individual grants.
          </p>
        ) : (
          <Table bordered size="sm" style={{ ...tableStyle, maxWidth: 500 }}>
            <tbody>
              {managers.map((u) => (
                <tr key={u.id}>
                  <td>{u.email || u.username}</td>
                  <td style={{ width: 80, textAlign: "center" }}>
                    <Button color="danger" size="sm" disabled={revokingId === u.id} onClick={() => this.onRevoke(u.id)}>
                      {revokingId === u.id ? "…" : "Revoke"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <h6 className="mt-4 mb-2" style={{ fontSize: "0.8rem" }}>
          Add a user
        </h6>
        <div className="d-flex gap-2 mb-3" style={{ maxWidth: 500 }}>
          <Input
            type="text"
            placeholder="Search by email or username"
            value={searchQuery}
            onChange={this.onSearchQueryChange}
            onKeyDown={this.onSearchKeyDown}
          />
          <Button color="primary" onClick={this.onSearch} disabled={searching || !searchQuery.trim()}>
            {searching ? "…" : "Search"}
          </Button>
        </div>

        {searchResults.length > 0 && (
          <Table bordered size="sm" style={{ ...tableStyle, maxWidth: 500 }}>
            <tbody>
              {searchResults.map((u) => (
                <tr key={u.id}>
                  <td>{u.email || u.username}</td>
                  <td style={{ width: 120, textAlign: "center" }}>
                    {u.has_permission ? (
                      <Badge color="secondary">Already has access</Badge>
                    ) : (
                      <Button color="success" size="sm" disabled={grantingId === u.id} onClick={() => this.onGrant(u)}>
                        {grantingId === u.id ? "…" : "Grant"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    );
  }

  render() {
    const { lvmGestures, loading, saving } = this.state;
    if (loading) {
      return <Spinner color="primary" />;
    }
    return (
      <>
        <p className="mb-4">
          These settings will be applied to every store flagged as Key Account, for as long as the flag is on. Please
          edit with caution.
        </p>

        <div className="mb-5">
          <h6 className="text-uppercase mb-2">LVM Multiplier per Gesture</h6>
          <p className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
            Multiplies the alert priority score so the extra alerts generated by Key Account stores rise in the labeling
            queue.
          </p>
          <Table bordered size="sm" style={tableStyle}>
            <thead className="text-primary">
              <tr>
                <th style={headerCellStyle}>Gesture type</th>
                <th style={headerCellStyle}>LVM Multiplier</th>
              </tr>
            </thead>
            <tbody>
              {lvmGestures.map((gesture) => (
                <tr key={gesture.id}>
                  <td className="text-capitalize">{lvmGestureLabel(gesture.name)}</td>
                  <td>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={gesture.value ?? ""}
                      onChange={(e) => this.handleLvmChange(gesture.id, e.target.value)}
                      placeholder="Enter multiplier (float)"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className="mt-3">
          <Button color="success" onClick={this.onSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>

        {this.renderManagersSection()}
      </>
    );
  }
}

export default KeyAccountPresettingsTab;
